import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { DesktopDatePicker as MuiDatePicker } from "@mui/x-date-pickers";
import { TextField, FormHelperText, IconButton, Box } from "@mui/material";
import { Clear } from "@mui/icons-material";
import { ChevronDown, ChevronUp } from "lucide-react";
// For localization
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/ar-sa";

import messages from "../../../assets/locale/messages";
import { clx } from "../../../utils/Helpers";
import "./FilterDatePicker.scss";

const FilterDatePicker = ({
  value,
  onChange,
  name,
  id,
  placeholder,
  calendarViews,
  openView,
  wrapperClass,
  inputClass,
  helperTextClass,
  closeOnSelect,
  disabled,
  disableFuture,
  disablePast,
  disableMinToday,
  disableMaxToday,
  maxDate,
  minDate,
  inputDateFormat,
  shouldDisableDate,
  shouldDisableMonth,
  shouldDisableYear,
  shouldNotBeLessThanToday,
  readOnly,
  isInputHasErr,
  errMsg,
  helperText,
  preventManualDateEntry,
  clear,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const { inputsValidations, shared: sharedMsgs } = messages[lang];

  const [date, setDate] = useState(null);
  const [invalidDate, setInvalidDate] = useState(false);
  const [lessThanToday, setLessThanToday] = useState(false);
  const [futureDate, setFutureDate] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const today = new Date();

  const isDateLessThanToday = (enteredDate) => {
    let nowDate = new Date();
    let nowYear = +nowDate.getFullYear();
    let nowMonth = +nowDate.getMonth() + 1;
    let nowDay = +nowDate.getDate();
    let enteredDateYear = +enteredDate?.split("-")[0];
    let enteredDateMonth = +enteredDate?.split("-")[1];
    let enteredDateDay = +enteredDate?.split("-")[2]?.split("T")[0];

    if (
      enteredDateYear < nowYear ||
      (enteredDateYear === nowYear && enteredDateMonth < nowMonth) ||
      (enteredDateYear === nowYear &&
        enteredDateMonth === nowMonth &&
        enteredDateDay < nowDay)
    ) {
      return true;
    } else return false;
  };

  // To handle any required formatting on date
  useEffect(() => {
    setDate(value ? dayjs(value) : null);
  }, [value]);

  const handleDateChange = (selectedDate) => {
    if (selectedDate && dayjs(selectedDate).format() === "Invalid Date") {
      setInvalidDate(true);
      setDate(selectedDate);
    } else if (
      shouldNotBeLessThanToday &&
      isDateLessThanToday(dayjs(selectedDate).format())
    ) {
      setInvalidDate(false);
      setLessThanToday(true);
      setDate(selectedDate);
    } else if (
      disableFuture &&
      selectedDate &&
      dayjs(selectedDate).isAfter(dayjs(), "day")
    ) {
      setInvalidDate(false);
      setFutureDate(true);
      setDate(selectedDate);
    } else {
      setInvalidDate(false);
      setLessThanToday(false);
      setFutureDate(false);
      onChange(selectedDate ? dayjs(selectedDate).format() : "");
    }
  };

  useEffect(() => {
    if (clear) {
      handleResetValue();
    }
  }, [clear]);

  const handleResetValue = () => {
    handleDateChange(null);
    setDate(null);
    setInvalidDate(false);
  };

  const inputPlaceholder =
    placeholder != null && String(placeholder).trim() !== ""
      ? placeholder
      : sharedMsgs.dateInputFormatPlaceholder;

  return (
    <div
      className={clx(
        "filter-date-picker-wrapper",
        wrapperClass,
      )}
    >
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        adapterLocale={lang === "en" ? "en" : "ar-sa"}
      >
        <MuiDatePicker
          value={date}
          onChange={handleDateChange}
          onOpen={() => setIsOpen(true)}
          onClose={() => setIsOpen(false)}
          name={name}
          id={id}
          className={`custom-date-picker ${inputClass}`}
          views={calendarViews}
          openTo={openView}
          closeOnSelect={closeOnSelect}
          disabled={disabled}
          disableFuture={disableFuture}
          disablePast={disablePast}
          maxDate={
            disableMaxToday ? today.setDate(today.getDate() - 1) : maxDate
          }
          minDate={
            disableMinToday ? today.setDate(today.getDate() + 1) : minDate
          }
          inputFormat={inputDateFormat}
          readOnly={readOnly}
          shouldDisableDate={shouldDisableDate}
          shouldDisableMonth={shouldDisableMonth}
          shouldDisableYear={shouldDisableYear}
          components={{
            OpenPickerIcon: isOpen ? ChevronUp : ChevronDown,
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              className={clx("ui-input", value && "ui-input--filled")}
              inputProps={{
                ...params.inputProps,
                placeholder: inputPlaceholder,
              }}
              error={invalidDate || lessThanToday || futureDate || isInputHasErr}
              InputProps={{
                ...params.InputProps,
                readOnly: preventManualDateEntry,
                endAdornment: (
                  <Box className="filter-date-picker-wrapper__end-icons">
                    {value ? (
                      <IconButton
                        className="close-btn"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleResetValue();
                        }}
                        aria-label="clear"
                        size="small"
                      >
                        <Clear className="clear-icon" />
                      </IconButton>
                    ) : null}
                    {params.InputProps?.endAdornment}
                  </Box>
                ),
              }}
            />
          )}
        />
      </LocalizationProvider>
      <FormHelperText
        error={invalidDate || lessThanToday || futureDate || isInputHasErr}
        className={`${helperTextClass || "font-medium fsize-12"} ${
          lang === "en" ? "" : "text-end"
        }`}
      >
        {invalidDate
          ? inputsValidations["invalidDate"]
          : lessThanToday
          ? inputsValidations["shouldNotBeBeforeToday"]
          : futureDate
          ? inputsValidations["dateCannotBeFuture"]
          : isInputHasErr
          ? inputsValidations[errMsg]
          : helperText}
      </FormHelperText>
    </div>
  );
};

FilterDatePicker.propTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  id: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  calendarViews: PropTypes.arrayOf(PropTypes.string),
  openView: PropTypes.oneOf(["day", "month", "year"]),
  wrapperClass: PropTypes.string,
  inputClass: PropTypes.string,
  helperTextClass: PropTypes.string,
  closeOnSelect: PropTypes.bool,
  disabled: PropTypes.bool,
  disableFuture: PropTypes.bool,
  disablePast: PropTypes.bool,
  disableMinToday: PropTypes.bool,
  disableMaxToday: PropTypes.bool,
  maxDate: PropTypes.any,
  minDate: PropTypes.any,
  inputDateFormat: PropTypes.string,
  shouldDisableDate: PropTypes.func,
  shouldDisableMonth: PropTypes.func,
  shouldDisableYear: PropTypes.func,
  shouldNotBeLessThanToday: PropTypes.bool,
  readOnly: PropTypes.bool,
  isInputHasErr: PropTypes.bool,
  clear: PropTypes.bool,
  errMsg: PropTypes.string,
  helperText: PropTypes.string,
  preventManualDateEntry: PropTypes.bool,
  showLabel: PropTypes.bool,
};

FilterDatePicker.defaultProps = {
  calendarViews: ["year", "month", "day"],
  openView: "day",
  closeOnSelect: true,
  inputDateFormat: "DD/MM/YYYY",
  showLabel: false,
};

export default FilterDatePicker;
